import { Button } from 'components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/Dialog'
import { Calendar, Check, Clock, Loader2, Send } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { extractApiError } from '../../creative/utils/extract-api-error'
import { useScheduleTimezone } from '../../common/hooks/use-brand-timezone'
import {
  buildScheduledInstant,
  formatScheduledLabel,
  isTimeOptionInPast,
  todayInTz,
} from '../../common/utils/business-timezone'
import {
  getAvailableTimeOptions,
  getDefaultSchedule,
} from '../../common/utils/schedule-time-options'

interface ScheduleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSchedule: (scheduledAt: string) => void | Promise<void>
  onUnschedule?: () => void
  onPostNow?: () => void
  initialDate?: string // YYYY-MM-DD
  initialTime?: string // HH:MM
  /**
   * Overrides the idle confirm-button label (default: "Reschedule" when
   * `onUnschedule` is set, else "Schedule"). Used by the approval flow to read
   * "Approve & schedule".
   */
  confirmLabel?: string
}

export function ScheduleDialog({
  open,
  onOpenChange,
  onSchedule,
  onUnschedule,
  onPostNow,
  initialDate,
  initialTime,
  confirmLabel,
}: ScheduleDialogProps) {
  const timezone = useScheduleTimezone()
  const today = todayInTz(timezone)
  const fallback = getDefaultSchedule(timezone)
  const [date, setDate] = useState(initialDate ?? fallback.date)
  const [time, setTime] = useState(initialTime ?? fallback.time)
  // Why the schedule was refused, shown inline. A refusal is usually
  // actionable and specific ("Pinterest won't accept this post: the description
  // is 1,544 characters and Pinterest allows 800. Shorten the description by
  // 744 characters."), which is too much to put in a toast that fades: it
  // belongs next to the button the user is about to press again.
  const [error, setError] = useState<string | null>(null)
  // 'confirming_post_now': "Post now instead" on a post that already holds a slot
  // is irreversible, so it asks once, inline, naming the committed slot it would
  // drop (see the early-publish guard in PublishLifecycleService.loadAndValidate).
  // Living in the status union means the open-effect reset covers it.
  const [status, setStatus] = useState<'idle' | 'confirming_post_now' | 'saving' | 'success'>(
    'idle'
  )
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Time-of-day slots still selectable for the chosen day — past hours are
  // dropped when `date` is today (in the brand TZ) so we never offer a slot
  // that has already passed.
  const availableTimeOptions = useMemo(
    () => getAvailableTimeOptions(date, timezone),
    [date, timezone]
  )

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    }
  }, [])

  // Sync internal state when dialog opens with new initial values. Defaults are
  // computed fresh so a dialog opened later in the day never starts on a slot
  // that's already in the past.
  useEffect(() => {
    if (open) {
      const next = getDefaultSchedule(timezone)
      setDate(initialDate ?? next.date)
      setTime(initialTime ?? next.time)
      setStatus('idle')
      setError(null)
    }
  }, [open, initialDate, initialTime, timezone])

  // Snap the selected time forward only when it's actually in the past (date
  // switched to today, or dialog reopened on a stale pick). A future time that
  // simply isn't on the grid (e.g. a 17:30 reschedule) is left untouched. Empty
  // list = whole day is gone; the confirm button guards against scheduling.
  // Fires on date/zone changes, not on the wall clock advancing while open.
  useEffect(() => {
    if (availableTimeOptions.length === 0) return
    if (isTimeOptionInPast(date, time, timezone)) {
      setTime(availableTimeOptions[0])
    }
  }, [availableTimeOptions, time, date, timezone])

  const isReschedule = !!onUnschedule
  // The slot the post actually holds (the caller seeds the picker with it), not
  // the live picker state: the confirm must name what Post now would drop even
  // after the user has moved the picker around.
  const committedSlotLabel =
    initialDate && initialTime
      ? formatScheduledLabel(buildScheduledInstant(initialDate, initialTime, timezone), timezone)
      : null

  const handleConfirm = useCallback(async () => {
    const scheduledAt = buildScheduledInstant(date, time, timezone)
    setStatus('saving')
    setError(null)
    try {
      await onSchedule(scheduledAt)
      setStatus('success')
      closeTimerRef.current = setTimeout(() => onOpenChange(false), 1200)
    } catch (err) {
      // A rejected `onSchedule` is the failure signal. The bare `catch` here
      // used to discard the reason and drop back to idle with nothing on
      // screen, so a handler that swallowed its own error and resolved made
      // this dialog flash a green "Scheduled!" and auto-close on a post the
      // server had just refused. Callers must REJECT to fail; we say why.
      setStatus('idle')
      setError(extractApiError(err, "Couldn't schedule. Please try again."))
    }
  }, [date, time, timezone, onSchedule, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" variant="dark" withCloseButton>
        <DialogHeader>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-acid">
            <Calendar size={24} />
          </div>
          <DialogTitle variant="dark">
            {isReschedule ? 'Reschedule post' : 'Schedule post'}
          </DialogTitle>
          <DialogDescription variant="dark">
            {isReschedule
              ? 'Change when this post will be published, or unschedule it.'
              : 'Pick a date and time for your post to go live.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Date picker */}
          <div>
            <label className="mono mb-1 flex items-center gap-1.5 text-[10px] tracking-[0.18em] text-paper-faint uppercase">
              <Calendar size={12} />
              Date
            </label>
            <input
              type="date"
              value={date}
              min={today}
              onChange={e => setDate(e.target.value)}
              className="w-full rounded-[8px] border border-line-bright bg-bg-3 px-3 py-2 text-[13px] text-paper [color-scheme:dark] transition-[border-color] outline-none focus:!border-acid"
            />
          </div>

          {/* Time picker */}
          <div>
            <label className="mono mb-1 flex items-center gap-1.5 text-[10px] tracking-[0.18em] text-paper-faint uppercase">
              <Clock size={12} />
              Time
            </label>
            <select
              value={time}
              onChange={e => setTime(e.target.value)}
              disabled={availableTimeOptions.length === 0}
              className="w-full rounded-[8px] border border-line-bright bg-bg-3 px-3 py-2 text-[13px] text-paper [color-scheme:dark] transition-[border-color] outline-none focus:!border-acid disabled:opacity-50"
            >
              {availableTimeOptions.length === 0 ? (
                <option value="">No times left today</option>
              ) : (
                availableTimeOptions.map(t => (
                  <option key={t} value={t}>
                    {formatTime(t)}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {error && (
            <p className="alert w-full text-[12px]" data-tone="error" role="alert">
              {error}
            </p>
          )}
          {status !== 'confirming_post_now' && (
            <Button
              variant="t3-primary"
              size="t3-md"
              onClick={handleConfirm}
              disabled={status !== 'idle' || availableTimeOptions.length === 0}
              className={`w-full transition-all duration-300 ${status === 'success' ? '!bg-[color:var(--success)] hover:!bg-[color:var(--success)]' : ''}`}
            >
              {status === 'saving' && <Loader2 size={14} className="animate-spin" />}
              {status === 'success' && <Check size={14} />}
              {status === 'idle' && <Calendar size={14} />}
              {status === 'saving' && 'Scheduling...'}
              {status === 'success' && 'Scheduled!'}
              {status === 'idle' && (confirmLabel ?? (isReschedule ? 'Reschedule' : 'Schedule'))}
            </Button>
          )}
          {status === 'idle' && onPostNow && (
            <Button
              variant="t3-tertiary"
              onClick={() =>
                isReschedule && committedSlotLabel ? setStatus('confirming_post_now') : onPostNow()
              }
              className="w-full"
            >
              <Send size={14} />
              Post now instead
            </Button>
          )}
          {status === 'confirming_post_now' && onPostNow && (
            <div
              role="group"
              aria-label="Confirm post now"
              className="flex w-full flex-col gap-2 rounded-[10px] border border-line-bright bg-bg-3 px-3 py-2"
            >
              <p className="text-[12px] text-paper-dim">
                Post now and drop the {committedSlotLabel} slot? It publishes right away and can’t
                be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="t3-tertiary"
                  size="t3-sm"
                  autoFocus
                  onClick={() => setStatus('idle')}
                >
                  Keep the schedule
                </Button>
                <Button variant="t3-primary" size="t3-sm" onClick={onPostNow}>
                  <Send size={14} />
                  Post now
                </Button>
              </div>
            </div>
          )}
          {status === 'idle' && onUnschedule && (
            <button
              onClick={onUnschedule}
              className="w-full rounded-[10px] px-4 py-2 text-[13px] font-medium text-[color:var(--alert-error-text)] transition-colors hover:bg-[color:var(--alert-error-bg)]"
            >
              Unschedule
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Human label for a scheduled instant ("Jun 22 at 9:00 AM"), rendered in the
 * business timezone. Thin re-export of `formatScheduledLabel` kept here so the
 * many existing composer importers don't all have to change their import path;
 * callers MUST pass the resolved zone (e.g. `useBrandTimezone()`).
 */
export function formatScheduledDate(scheduledAt: string, timezone: string): string {
  return formatScheduledLabel(scheduledAt, timezone)
}

function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const hour = h % 12 || 12
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`
}
